import { Platform } from '@angular/cdk/platform';
import { AfterViewInit, ChangeDetectorRef, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Track } from '@app/core/models/Track';
import { HotkeysService } from '@app/core/services/hotkeys.service';
import { TracksTableDirective } from '@app/library/components/content/tracks-table/tracks-table.directive';
import { RootBac } from '@app/library/models/bac.model';
import { LibraryModalService } from '@app/library/services/library-modal.service';
import { ToggleColumn } from '@app/library/states/columns.actions';
import { ResetFilterForBacCurrentlyEditing } from '@app/library/states/filters.actions';
import {
  SelectTrack,
  UnselectAll,
  UnselectTrack,
} from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import {
  AddDefaultTracksSuccess,
  TracksRequest,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { CloseCurrentPanel } from '@radioking/shared/pannel-manager';
import { Observable } from 'rxjs';
import { delay, first, map, throttleTime } from 'rxjs/operators';

import { ColumnShown } from '../../../models/column-shown.model';

@Component({
  selector: 'rk-library-audio-table',
  templateUrl: './library-audio-table.component.html',
  styleUrls: ['./library-audio-table.component.scss'],
})
export class LibraryAudioTableComponent
  extends TracksTableDirective
  implements AfterViewInit
{
  @Input()
  bacLength: number;

  @Input()
  isChronic = false;

  @Input()
  isCurrentlyFiltered = false;

  @Input()
  columns: ColumnShown;

  @Input()
  currentBacName: string;

  @Input()
  currentBac: RootBac;

  @Input()
  searchEvent$: Observable<void>;

  @Select(SelectedTracksState.selectedTracks)
  selectedTracks$: Observable<number[]>;

  @Select(SelectedTracksState.numberTracksSelected)
  numberTracksSelected$: Observable<number>;

  @Input()
  searchField$: Observable<string>;

  @Input()
  importingDefaultTracks: boolean;

  @Input()
  bacPlanLocked: boolean;

  checked = false;

  constructor(
    protected readonly store: Store,
    protected readonly modalService: LibraryModalService,
    protected readonly actions$: Actions,
    protected readonly dialog: MatDialog,
    protected readonly hotkeys: HotkeysService,
    protected readonly cdr: ChangeDetectorRef,
    private readonly platform: Platform,
  ) {
    super(store, modalService, actions$, dialog, hotkeys, cdr);
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(SelectTrack),
          map(acc => acc.track.id),
        )
        .subscribe(idTrack => {
          this.lastSelectedTrackId = idTrack;
        }),
    );
    this.subscription.add(
      this.actions$
        .pipe(
          ofActionDispatched(UnselectTrack),
          map(acc => acc.track.id),
        )
        .subscribe(idTrack => {
          if (this.lastSelectedTrackId === idTrack) {
            this.lastSelectedTrackId = -1;
          }
        }),
    );
    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(UnselectAll)).subscribe(() => {
        this.lastSelectedTrackId = -1;
      }),
    );

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(ToggleColumn), delay(100)).subscribe(() => {
        this.updateTagZoneSize();
      }),
    );

    this.subscription.add(
      this.tracksObservable$.subscribe(res => {
        // Compute the size to display tags
        // Useful if the library was empty
        if (res.length > 0 && this.tagDisplayedLimiter === 0) {
          setTimeout(() => {
            this.updateTagZoneSize();
          }, 100);
        }
      }),
    );

    this.subscription.add(
      this.actions$.pipe(ofActionDispatched(AddDefaultTracksSuccess)).subscribe(() => {
        this.store.dispatch(new TracksRequest(this.currentBac));
      }),
    );
    this.selectedTracksObserver(this.numberTracksSelected$);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.subscription.add(
      this.resizeObservable.pipe(throttleTime(250)).subscribe(() => {
        if (this.store.selectSnapshot(SelectedTracksState.numberTracksSelected) > 0) {
          this.updateHeaderActions();
        }
      }),
    );
  }

  completedTracksLength(tracks: Track[]): number {
    return tracks.filter(track => !track.processing).length;
  }

  getTableHeight(): number {
    if (this.importingDefaultTracks) {
      return 350;
    }
    // Size * line height + header height
    if (this.tracksObservable$.value.length) {
      return this.tracksObservable$.value.length * 50 + 43;
    }

    return 256;
  }

  detectScroll() {
    if (this.table && !this.platform.FIREFOX) {
      const totalTableHeight = this.getTableHeight() + 219;
      this.hasScroll = totalTableHeight > window.innerHeight;
    }
  }

  moveTracks() {
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    if (selectedTracks.length > 1) {
      this.modalService.moveMultipleTrack(selectedTracks, null, this.currentBac);
    } else {
      const track$ = this.store
        .select(TracksState.trackWithId)
        .pipe(map(fn => fn(selectedTracks[0])));
      track$.pipe(first()).subscribe(data => {
        this.modalService.moveTrack(data, null);
      });
    }
  }

  moveTrack(track: Track) {
    this.modalService.moveTrack(track, null);
  }

  archiveTrack(track: Track, shortcut = false) {
    if (this.currentBac.name === '__CHRONIC__') {
      return;
    }
    this.modalService.archiveTrack(track, this.currentBac, shortcut);
  }

  archiveMultiple(shortcut = false) {
    if (this.currentBac.name === '__CHRONIC__') {
      return;
    }
    const selectedTracks = this.store.selectSnapshot(SelectedTracksState.selectedTracks);
    if (selectedTracks.length < 2) {
      const track = this.store.selectSnapshot(TracksState.trackWithId)(selectedTracks[0]);
      this.archiveTrack(track, shortcut);
    } else {
      this.modalService.archiveMultipleTracks(
        this.store.selectSnapshot(SelectedTracksState.selectedTracks),
        this.currentBac,
        shortcut,
      );
    }
  }

  canRemoveTagsFromTracks = (track: Track) =>
    !this.isChronic && this.isNotLocked(track) && this.hasTags(track);

  @Dispatch()
  resetToDefault = () => [
    new ResetFilterForBacCurrentlyEditing(this.currentBac.id),
    new CloseCurrentPanel(true),
  ];
}
